import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  ColumnLayout,
  Header,
  Input,
  Pagination,
  SpaceBetween,
  Spinner,
  Table,
  TextFilter,
} from '@cloudscape-design/components';
import { getDataService } from '../services/dataServiceFactory';
import { listAccessibleTeams } from '../services/teamsService';

const PAGE_SIZE = 25;

const severityColor = (sev) => {
  switch ((sev || '').toUpperCase()) {
    case 'HIGH':
    case 'CRITICAL':
      return 'red';
    case 'MEDIUM':
      return 'blue';
    case 'LOW':
      return 'grey';
    default:
      return 'grey';
  }
};

const awsCatalogInfo = (control) =>
  (control?.additionalInfo ?? []).find(
    (a) => a?.kind === 'AWSControlCatalogAdditionalInfo',
  );

const fetchMappingsForControl = async (control) => {
  const svc = getDataService();
  const controlKey = control.controlKey;
  if (typeof svc.queryControlMappings === 'function') {
    const res = await svc.queryControlMappings({ controlKey, limit: 500 });
    return res?.items || [];
  }
  if (typeof svc.getMappingsForControl === 'function') {
    const res = await svc.getMappingsForControl(controlKey, {
      maxResults: 500,
    });
    return res?.mappings || [];
  }
  throw new Error('No mapping query method available on data service');
};

const ControlExplorer = () => {
  const [frameworks, setFrameworks] = useState([]);
  const [frameworksLoading, setFrameworksLoading] = useState(true);
  const [frameworksError, setFrameworksError] = useState(null);
  const [frameworkFilter, setFrameworkFilter] = useState('');
  const [frameworkPageIdx, setFrameworkPageIdx] = useState(1);

  const [selectedFramework, setSelectedFramework] = useState(null);

  const [controls, setControls] = useState([]);
  const [controlsLoading, setControlsLoading] = useState(false);
  const [controlsError, setControlsError] = useState(null);
  const [controlFilter, setControlFilter] = useState('');
  const [controlPageIdx, setControlPageIdx] = useState(1);

  const [selectedControl, setSelectedControl] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappingsError, setMappingsError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setFrameworksLoading(true);
    setFrameworksError(null);
    const svc = getDataService();
    const framePromise = svc.listFrameworks
      ? svc.listFrameworks()
      : svc.getAvailableFrameworks();
    Promise.all([Promise.resolve(framePromise), listAccessibleTeams().catch(() => [])])
      .then(([res, teams]) => {
        if (cancelled) return;
        const items = Array.isArray(res) ? res : res?.frameworks || [];
        const callerTeamIds = new Set(teams.map((t) => t.groupId));
        // Legacy frameworks without ownerBindleResource stay visible to
        // everyone. Scoped frameworks only appear if the caller is in the
        // owning team.
        const visible = items.filter((f) => {
          if (!f.ownerBindleResource) return true;
          return callerTeamIds.has(f.ownerBindleResource);
        });
        setFrameworks(visible);
      })
      .catch((err) => {
        if (!cancelled) setFrameworksError(err);
      })
      .finally(() => {
        if (!cancelled) setFrameworksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedFramework) return;
    let cancelled = false;
    setControlsLoading(true);
    setControlsError(null);
    setControls([]);
    setControlFilter('');
    setControlPageIdx(1);
    const frameworkKey =
      selectedFramework.frameworkKey ??
      `${selectedFramework.frameworkName}#${selectedFramework.version}`;
    getDataService()
      .getFrameworkControlsByKey(frameworkKey)
      .then((items) => {
        if (!cancelled) setControls(items || []);
      })
      .catch((err) => {
        if (!cancelled) setControlsError(err);
      })
      .finally(() => {
        if (!cancelled) setControlsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedFramework]);

  useEffect(() => {
    if (!selectedControl) return;
    let cancelled = false;
    setMappings([]);
    setMappingsLoading(true);
    setMappingsError(null);
    fetchMappingsForControl(selectedControl)
      .then((items) => {
        if (!cancelled) setMappings(items);
      })
      .catch((err) => {
        if (!cancelled) setMappingsError(err);
      })
      .finally(() => {
        if (!cancelled) setMappingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedControl]);

  const filteredFrameworks = useMemo(() => {
    const q = frameworkFilter.trim().toLowerCase();
    if (!q) return frameworks;
    return frameworks.filter((f) => {
      const hay = `${f.frameworkName} ${f.version} ${f.frameworkKey ?? ''} ${
        f.description ?? ''
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [frameworks, frameworkFilter]);

  const pagedFrameworks = useMemo(() => {
    const start = (frameworkPageIdx - 1) * PAGE_SIZE;
    return filteredFrameworks.slice(start, start + PAGE_SIZE);
  }, [filteredFrameworks, frameworkPageIdx]);

  const filteredControls = useMemo(() => {
    const q = controlFilter.trim().toLowerCase();
    if (!q) return controls;
    return controls.filter((c) => {
      const hay = `${c.controlId ?? ''} ${c.title ?? ''} ${
        c.description ?? ''
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [controls, controlFilter]);

  const pagedControls = useMemo(() => {
    const start = (controlPageIdx - 1) * PAGE_SIZE;
    return filteredControls.slice(start, start + PAGE_SIZE);
  }, [filteredControls, controlPageIdx]);

  // --- Control detail view
  if (selectedControl) {
    const info = awsCatalogInfo(selectedControl);
    return (
      <SpaceBetween size="l">
        <Header
          variant="h1"
          actions={
            <Button onClick={() => setSelectedControl(null)}>
              Back to controls
            </Button>
          }
          description={selectedControl.title}
        >
          {selectedControl.controlId || selectedControl.controlKey}
        </Header>

        <Container header={<Header variant="h2">Details</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Framework</Box>
              <div>{selectedControl.frameworkKey}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Status</Box>
              <div>{selectedControl.status || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Source</Box>
              <div>{selectedControl.source || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Severity</Box>
              <div>
                {info?.severity ? (
                  <Badge color={severityColor(info.severity)}>
                    {info.severity}
                  </Badge>
                ) : (
                  '-'
                )}
              </div>
            </div>
            {selectedControl.description && (
              <div style={{ gridColumn: '1 / span 2' }}>
                <Box variant="awsui-key-label">Description</Box>
                <div>{selectedControl.description}</div>
              </div>
            )}
          </ColumnLayout>
        </Container>

        {mappingsError && (
          <Alert type="error" header="Failed to load mappings">
            {String(mappingsError.message || mappingsError)}
          </Alert>
        )}

        <Table
          variant="container"
          loading={mappingsLoading}
          loadingText="Loading mappings..."
          header={
            <Header counter={mappings.length ? `(${mappings.length})` : ''}>
              Framework mappings
            </Header>
          }
          items={mappings}
          columnDefinitions={[
            {
              id: 'mappedControlKey',
              header: 'Mapped control',
              cell: (m) => m.mappedControlKey,
            },
            {
              id: 'workflow',
              header: 'Source',
              cell: (m) => m.mappingWorkflowKey || '-',
              width: 220,
            },
            {
              id: 'status',
              header: 'Status',
              cell: (m) => m.status || '-',
              width: 140,
            },
            {
              id: 'timestamp',
              header: 'Updated',
              cell: (m) =>
                m.timestamp ? new Date(m.timestamp).toLocaleString() : '-',
            },
          ]}
          empty={
            <Box textAlign="center" padding="l">
              <b>No mappings</b>
              <Box variant="small">
                This control isn't mapped to any other framework yet.
              </Box>
            </Box>
          }
        />
      </SpaceBetween>
    );
  }

  // --- Controls-in-framework view
  if (selectedFramework) {
    return (
      <SpaceBetween size="l">
        <Header
          variant="h1"
          actions={
            <Button onClick={() => setSelectedFramework(null)}>
              Back to frameworks
            </Button>
          }
          description={
            selectedFramework.description ||
            `${selectedFramework.frameworkName} · ${selectedFramework.version}`
          }
        >
          {selectedFramework.frameworkName}{' '}
          <Badge>{selectedFramework.version}</Badge>
        </Header>

        {controlsError && (
          <Alert type="error" header="Failed to load controls">
            {String(controlsError.message || controlsError)}
          </Alert>
        )}

        <Table
          variant="container"
          resizableColumns
          loading={controlsLoading}
          loadingText="Loading controls..."
          header={
            <Header
              counter={
                controls.length
                  ? `(${filteredControls.length} of ${controls.length})`
                  : undefined
              }
            >
              Controls
            </Header>
          }
          items={pagedControls}
          filter={
            <TextFilter
              filteringText={controlFilter}
              onChange={({ detail }) => {
                setControlFilter(detail.filteringText);
                setControlPageIdx(1);
              }}
              filteringPlaceholder="Filter controls"
            />
          }
          pagination={
            <Pagination
              currentPageIndex={controlPageIdx}
              pagesCount={Math.max(
                1,
                Math.ceil(filteredControls.length / PAGE_SIZE),
              )}
              onChange={({ detail }) =>
                setControlPageIdx(detail.currentPageIndex)
              }
            />
          }
          columnDefinitions={[
            {
              id: 'controlId',
              header: 'Control ID',
              cell: (c) => (
                <Button
                  variant="link"
                  onClick={() => setSelectedControl(c)}
                >
                  {c.controlId || c.controlKey}
                </Button>
              ),
              minWidth: 200,
              width: 260,
            },
            {
              id: 'title',
              header: 'Title',
              cell: (c) => c.title || '-',
              minWidth: 240,
            },
            {
              id: 'severity',
              header: 'Severity',
              cell: (c) => {
                const sev = awsCatalogInfo(c)?.severity;
                return sev ? (
                  <Badge color={severityColor(sev)}>{sev}</Badge>
                ) : (
                  '-'
                );
              },
              width: 120,
            },
            {
              id: 'status',
              header: 'Status',
              cell: (c) => c.status || '-',
              width: 120,
            },
            {
              id: 'description',
              header: 'Description',
              cell: (c) =>
                c.description ? (
                  <Box variant="p">{c.description}</Box>
                ) : (
                  '-'
                ),
              minWidth: 320,
            },
          ]}
          empty={
            <Box textAlign="center" padding="l">
              <b>No controls</b>
              <Box variant="small">
                This framework has no controls yet, or they failed to load.
              </Box>
            </Box>
          }
        />
      </SpaceBetween>
    );
  }

  // --- Framework list view
  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Browse frameworks and their controls. Pick a framework to drill in."
      >
        Control Explorer
      </Header>

      {frameworksError && (
        <Alert type="error" header="Failed to load frameworks">
          {String(frameworksError.message || frameworksError)}
        </Alert>
      )}

      <Container>
        <SpaceBetween size="s">
          <Input
            type="search"
            placeholder="Filter frameworks"
            value={frameworkFilter}
            onChange={({ detail }) => {
              setFrameworkFilter(detail.value);
              setFrameworkPageIdx(1);
            }}
          />
          {frameworksLoading ? (
            <Box textAlign="center" padding="l">
              <Spinner /> Loading frameworks...
            </Box>
          ) : (
            <Table
              variant="embedded"
              resizableColumns
              items={pagedFrameworks}
              pagination={
                <Pagination
                  currentPageIndex={frameworkPageIdx}
                  pagesCount={Math.max(
                    1,
                    Math.ceil(filteredFrameworks.length / PAGE_SIZE),
                  )}
                  onChange={({ detail }) =>
                    setFrameworkPageIdx(detail.currentPageIndex)
                  }
                />
              }
              columnDefinitions={[
                {
                  id: 'frameworkName',
                  header: 'Framework',
                  cell: (f) => (
                    <Button
                      variant="link"
                      onClick={() => setSelectedFramework(f)}
                    >
                      {f.frameworkName}
                    </Button>
                  ),
                  minWidth: 320,
                  width: 360,
                },
                {
                  id: 'version',
                  header: 'Version',
                  cell: (f) => f.version,
                  width: 120,
                },
                {
                  id: 'status',
                  header: 'Status',
                  cell: (f) => f.status || '-',
                  width: 120,
                },
                {
                  id: 'ownerTeam',
                  header: 'Owner team',
                  cell: (f) => f.ownerBindleResource || 'Shared',
                  width: 180,
                },
                {
                  id: 'description',
                  header: 'Description',
                  cell: (f) => f.description || '-',
                  minWidth: 320,
                },
              ]}
              empty={
                <Box textAlign="center" padding="l">
                  <b>No frameworks</b>
                  <Box variant="small">
                    None loaded yet. Wait for the weekly ControlCatalog sync
                    or ingest a framework.
                  </Box>
                </Box>
              }
            />
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

export default ControlExplorer;
